import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const WealthStabilityDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [assetTypeFilter, setAssetTypeFilter] = useState('All Asset Types');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Mock Data for Assets (Simulating Data Mapping from customized API response) --
    // In a real scenario, this would likely come from getDashboardData or a dedicated assets endpoint
    const mockAssets = [
        { key: '1', asset: 'Jamnagar Refinery', entity: 'RIL', type: 'Infrastructure', value: '1,50,000', liquidity: 'Low', health: 5 },
        { key: '2', asset: '4G/5G Spectrum (Jio)', entity: 'Jio Platforms', type: 'Telecom', value: '1,20,000', liquidity: 'Low', health: 4 },
        { key: '3', asset: 'Retail Stores Network', entity: 'Reliance Retail', type: 'Retail', value: '30,000', liquidity: 'Medium', health: 5 },
        { key: '4', asset: 'Renewable Projects', entity: 'Reliance New Energy', type: 'Energy', value: '25,000', liquidity: 'Low', health: 3 },
    ];

    const filteredAssets = mockAssets.filter(item => {
        const matchesSearch = item.asset.toLowerCase().includes(searchText.toLowerCase()) || item.entity.toLowerCase().includes(searchText.toLowerCase());
        const matchesType = assetTypeFilter === 'All Asset Types' || item.type === assetTypeFilter;
        return matchesSearch && matchesType;
    });

    const renderHealthBadge = (score: number) => {
        let color = '#52c41a'; // 5
        if (score === 4) color = '#1890ff';
        if (score === 3) color = '#faad14';
        if (score < 3) color = '#f5222d';
        return (
            <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color,
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    const renderLiquidityTag = (level: string) => {
        let color = 'default';
        if (level === 'High') color = 'success';
        if (level === 'Medium') color = 'warning';
        if (level === 'Low') color = 'error';
        return <Tag color={color} style={{ minWidth: '60px', textAlign: 'center' }}>{level}</Tag>;
    };

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Wealth</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Asset Table */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Wealth Stability & Entity Exposure</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search asset..."
                                    style={{ width: 200 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Asset Types" onChange={setAssetTypeFilter} style={{ width: 150 }}>
                                    <Option value="All Asset Types">All Asset Types</Option>
                                    <Option value="Infrastructure">Infrastructure</Option>
                                    <Option value="Telecom">Telecom</Option>
                                    <Option value="Retail">Retail</Option>
                                    <Option value="Energy">Energy</Option>
                                </Select>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredAssets.length} of {mockAssets.length} assets</Text>

                        <Table
                            dataSource={filteredAssets}
                            pagination={false}
                            columns={[
                                { title: 'ASSET', dataIndex: 'asset', key: 'asset', render: (text) => <b>{text}</b> },
                                { title: 'ENTITY', dataIndex: 'entity', key: 'entity', render: (text) => <Text type="secondary">{text}</Text> },
                                { title: 'TYPE', dataIndex: 'type', key: 'type' },
                                { title: 'VALUE (₹CR)', dataIndex: 'value', key: 'value', render: (text) => <b>{text}</b> },
                                { title: 'LIQUIDITY', dataIndex: 'liquidity', key: 'liquidity', align: 'center', render: (text) => renderLiquidityTag(text) },
                                { title: 'HEALTH', dataIndex: 'health', key: 'health', align: 'center', render: (score) => renderHealthBadge(score) },
                                { title: 'ACTION', key: 'action', align: 'center', render: () => <a href="#">View</a> }
                            ]}
                        />

                        {/* KPI Summaries below table */}
                        <Row gutter={16} className="mt-4">
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Total Asset Value</Text>
                                    <Title level={3} className="text-primary m-0">₹3,25,000cr</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">Average Health</Text>
                                    <Title level={3} className="text-success m-0">4.3 / 5</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff0f6' }}>
                                    <Text type="secondary" className="d-block mb-1">High Liquidity Assets</Text>
                                    <Title level={3} style={{ color: '#eb2f96', margin: 0 }}>1</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default WealthStabilityDashboard;
